// ---------------------------------------------------------------------------
// Knowledge Hub API client — /api/v2/knowledge (ztd_app/api/modules/knowledge_hub).
//
// One catalog over every knowledge store (Project Knowledge Base, Conversational
// AI KB, MDM KB, ZTD-provided packs, tenant-shared hub documents) and the
// project / per-agent selection model. Phase 1: context selections and budgets
// are recorded and audited, and take effect when each agent reads through the
// hub (Phase 2); executable selection (DQ packs) takes effect at the next run.
// ---------------------------------------------------------------------------

import { clearAuth } from "../auth";

const BASE = "/api/v2/knowledge";
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function csrfToken(): string {
  return document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "";
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const isForm = init?.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isForm ? {} : { "Content-Type": "application/json" }),
    ...(CSRF_REQUIRED_METHODS.has(method)
      ? { "X-CSRF-Token": csrfToken() }
      : {}),
    ...(init?.headers as Record<string, string>),
  };
  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (res.status === 401) {
    clearAuth();
    throw new Error("Session expired — please log in again.");
  }
  const body = await res.json().catch(() => ({ detail: res.statusText }));
  if (!res.ok) {
    const detail = body?.detail;
    throw new Error(
      typeof detail === "string"
        ? detail
        : body?.message || `Request failed: ${res.status}`,
    );
  }
  return (body?.data ?? body) as T;
}

// ---------------------------------------------------------------------------
// Types (mirror knowledge_hub/catalog.py KnowledgeItem.to_dict and
// service.KnowledgeHubService.project_view)
// ---------------------------------------------------------------------------

export type Visibility =
  | "ztd_provided"
  | "tenant_shared"
  | "project_private"
  | "unavailable";

export interface KnowledgeItem {
  ref: string;
  kind: "document" | "pack" | "unavailable";
  store: string;
  visibility: Visibility;
  knowledge_class: "context" | "executable" | "none";
  title: string;
  owner_project_id: string | null;
  doc_type: string | null;
  status: string;
  classification_tier: number | null;
  llm_allowed: boolean;
  applies_to_agents: string[];
  version: string | null;
  size_estimate_tokens: number;
  chunk_count: number | null;
  created_by: string | null;
  created_at: string | null;
  default_on_since: string | null;
  managed_by: string | null;
  details: Record<string, unknown>;
}

export interface AgentChoice {
  included: boolean;
  source: "default" | "project" | "agent";
  blocked_reason: string | null;
}

export interface ContextItem extends KnowledgeItem {
  selection: Record<string, AgentChoice>;
}

export interface PackItem extends KnowledgeItem {
  selected: boolean;
}

export interface HubAgent {
  key: string;
  label: string;
  reads_through_hub: boolean;
  reads_through_hub_from_phase: number | null;
  budget: number | null;
  default_budget: number;
}

export interface EngagementBinding {
  engagement_slug: string | null;
  /** null = every pack under the engagement; [] = no pack is evaluated. */
  dq_rule_packs: string[] | null;
  pack_filter_valid: boolean;
  binding: "auto" | "pinned";
}

export interface ProjectKnowledgeView {
  project: {
    id: string;
    name: string;
    slug: string;
    created_at: string | null;
  };
  agents: HubAgent[];
  context_items: ContextItem[];
  executable: {
    binding: EngagementBinding;
    packs: PackItem[];
    engagements: { slug: string; packs: string[] }[];
    actor_project_role: string | null;
  };
  reference_packs: KnowledgeItem[];
  unavailable_sources: KnowledgeItem[];
  permissions: {
    can_select_context: boolean;
    can_select_executable: boolean;
    can_promote: boolean;
  };
  limits: { min_budget: number; max_budget: number };
}

export interface PromotionRecord {
  id: string;
  source_ref: string;
  source_project_id: string;
  target_document_id: string;
  /** null when the copy has since been deleted (the record is kept). */
  target_title: string | null;
  justification: string;
  attestation: string;
  promoted_by: string;
  promoted_at: string;
}

export type SelectionMode = "include" | "exclude" | "default";

export const TENANT_DOC_TYPES: { value: string; label: string }[] = [
  { value: "regulation", label: "Regulation" },
  { value: "enterprise_standard", label: "Enterprise standard" },
  { value: "glossary", label: "Glossary" },
  { value: "data_dictionary", label: "Data dictionary" },
  { value: "data_model", label: "Data model" },
  { value: "functional_spec", label: "Functional spec" },
  { value: "other", label: "Other" },
];

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const knowledgeHub = {
  catalog: (scope: "all" | "tenant" = "all") =>
    request<KnowledgeItem[]>(`/catalog?scope=${scope}`),
  projectView: (projectId: string) =>
    request<ProjectKnowledgeView>(`/projects/${projectId}`),
  setSelection: (
    projectId: string,
    body: {
      agent_key: string;
      item_ref: string;
      mode: SelectionMode;
      reason?: string;
    },
  ) =>
    request<{ mode: SelectionMode | null }>(
      `/projects/${projectId}/selections`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
  setBudget: (
    projectId: string,
    agentKey: string,
    maxContextTokens: number | null,
  ) =>
    request(`/projects/${projectId}/budgets`, {
      method: "PUT",
      body: JSON.stringify({
        agent_key: agentKey,
        max_context_tokens: maxContextTokens,
      }),
    }),
  setExecutable: (
    projectId: string,
    body: {
      engagement_slug: string | null;
      dq_rule_packs: string[] | null;
      binding: "auto" | "pinned";
      reason?: string;
    },
  ) =>
    request<{ before: EngagementBinding; after: EngagementBinding }>(
      `/projects/${projectId}/executable`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
  uploadTenantDocument: (
    file: File,
    docType: string,
    title: string,
    defaultOn: boolean,
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("doc_type", docType);
    if (title) form.append("title", title);
    form.append("default_on", defaultOn ? "true" : "false");
    return request<KnowledgeItem>("/tenant/documents", {
      method: "POST",
      body: form,
    });
  },
  setDefaultOn: (documentId: string, defaultOn: boolean) =>
    request<KnowledgeItem>(`/tenant/documents/${documentId}`, {
      method: "PATCH",
      body: JSON.stringify({ default_on: defaultOn }),
    }),
  deleteTenantDocument: (documentId: string) =>
    request(`/tenant/documents/${documentId}`, { method: "DELETE" }),
  reindexTenantDocument: (documentId: string) =>
    request<KnowledgeItem>(`/tenant/documents/${documentId}/reindex`, {
      method: "POST",
    }),
  clearTenantDocumentReview: (documentId: string) =>
    request<KnowledgeItem>(`/tenant/documents/${documentId}/clear-review`, {
      method: "POST",
    }),
  attestation: () =>
    request<{ attestation: string; min_justification: number }>(
      "/promotions/attestation",
    ),
  promotions: () => request<PromotionRecord[]>("/promotions"),
  promote: (body: {
    source_ref: string;
    justification: string;
    attested: boolean;
    title: string;
  }) =>
    request<{ document: KnowledgeItem; promotion_id: string }>("/promotions", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

/** The document id of a ``doc:hub:<uuid>`` ref. */
export function hubDocumentId(ref: string): string {
  return ref.split(":").pop() ?? "";
}

export const VISIBILITY_LABELS: Record<Visibility, string> = {
  ztd_provided: "ZTD-provided",
  tenant_shared: "Tenant-shared",
  project_private: "Project-private",
  unavailable: "Unavailable",
};

export const STORE_LABELS: Record<string, string> = {
  pkb: "Project Knowledge Base",
  cai: "Conversational AI KB",
  mdm: "MDM KB",
  hub: "Knowledge Hub",
  pack: "ZTD pack",
};
